import {
    CSSProperties,
    ReactNode,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

interface Props {
    children: ReactNode;
    durationMs?: number;
    className?: string;
}

const AnimatedHeight = ({ children, durationMs = 150, className }: Props) => {
    const innerRef = useRef<HTMLDivElement | null>(null);
    const [height, setHeight] = useState<number | null>(null);

    // Seed the height from the initial render so the first frame matches the content
    // and subsequent height changes animate from that baseline.
    useLayoutEffect(() => {
        if (innerRef.current) {
            setHeight(innerRef.current.getBoundingClientRect().height);
        }
    }, []);

    useEffect(() => {
        const inner = innerRef.current;
        if (!inner) return undefined;
        const observer = new ResizeObserver((entries) => {
            const entry = entries?.length > 0 ? entries[0] : null;
            if (!entry) return;
            setHeight(entry.contentRect.height);
        });
        observer.observe(inner);
        return () => observer.disconnect();
    }, []);

    const style: CSSProperties =
        height == null
            ? {}
            : {
                  height,
                  overflow: 'hidden',
                  transition: `height ${durationMs}ms ease`,
              };

    return (
        <div style={style} className={className}>
            <div ref={innerRef}>{children}</div>
        </div>
    );
};

export default AnimatedHeight;
